import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PublicListing } from '@/contexts/SearchContext';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/utils/api-client';
import { CreateInquiryRequest } from '@/hooks/api/useBookingInquiries';

interface ContactHostFormData {
  checkInDate: Date | null;
  checkOutDate: Date | null;
  guestCount: number;
  whatsappNumber: string;
  instagramId: string;
  additionalNotes: string;
}

interface ContactHostFormProps {
  listing: PublicListing;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialData?: Partial<ContactHostFormData>;
}

interface FormErrors {
  [key: string]: string;
}

const ContactHostForm: React.FC<ContactHostFormProps> = ({
  listing,
  isOpen,
  onClose,
  onSuccess,
  initialData = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userProfile } = useAuth();

  const [formData, setFormData] = useState<ContactHostFormData>({
    checkInDate: initialData.checkInDate || null,
    checkOutDate: initialData.checkOutDate || null,
    guestCount: initialData.guestCount || 1,
    whatsappNumber: initialData.whatsappNumber || '',
    instagramId: initialData.instagramId || '',
    additionalNotes: initialData.additionalNotes || '',
  });

  // Update form data when initialData changes (when modal opens with prefilled data)
  React.useEffect(() => {
    if (isOpen && initialData) {
      setFormData(prev => ({
        ...prev,
        checkInDate: initialData.checkInDate || prev.checkInDate,
        checkOutDate: initialData.checkOutDate || prev.checkOutDate,
        guestCount: initialData.guestCount || prev.guestCount,
        whatsappNumber: initialData.whatsappNumber || prev.whatsappNumber,
        instagramId: initialData.instagramId || prev.instagramId,
        additionalNotes: initialData.additionalNotes || prev.additionalNotes,
      }));
    }
  }, [isOpen, initialData]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // Handle form field changes
  const handleFieldChange = useCallback((field: keyof ContactHostFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [formErrors]);

  // Handle guest count changes
  const handleGuestCountChange = useCallback((increment: boolean) => {
    setFormData(prev => ({
      ...prev,
      guestCount: increment 
        ? Math.min(prev.guestCount + 1, listing.maxGuests)
        : Math.max(prev.guestCount - 1, 1)
    }));
  }, [listing.maxGuests]);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const errors: FormErrors = {};
    
    if (!formData.checkInDate) {
      errors.checkInDate = 'Check-in date is required';
    }
    
    if (!formData.checkOutDate) {
      errors.checkOutDate = 'Check-out date is required';
    }
    
    if (formData.checkInDate && formData.checkOutDate) {
      if (formData.checkOutDate <= formData.checkInDate) {
        errors.checkOutDate = 'Check-out must be after check-in';
      }
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (formData.checkInDate < today) {
        errors.checkInDate = 'Check-in date cannot be in the past';
      }
    }
    
    if (!formData.whatsappNumber.trim()) {
      errors.whatsappNumber = 'WhatsApp number is required';
    } else if (!/^\+?[\d\s\-\(\)]+$/.test(formData.whatsappNumber.trim())) {
      errors.whatsappNumber = 'Please enter a valid phone number';
    }
    
    if (formData.guestCount > listing.maxGuests) {
      errors.guestCount = `Maximum ${listing.maxGuests} guests allowed`;
    }
    
    if (formData.guestCount < 1) {
      errors.guestCount = 'At least 1 guest is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, listing.maxGuests]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }
    
    // Check if user profile is available
    if (!userProfile) {
      setSubmitError('User profile not available. Please try refreshing the page.');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      
      const inquiryData: CreateInquiryRequest = {
        listingId: listing.listingId,
        listingTitle: listing.title, // Include denormalized listing title
        guestInfo: {
          name: userProfile.name || '',
          email: userProfile.email || '',
          whatsappNumber: formData.whatsappNumber.trim(),
          instagramId: formData.instagramId.trim() || undefined,
        },
        stayDetails: {
          checkInDate: formData.checkInDate!.toISOString().split('T')[0], // Convert to YYYY-MM-DD format
          checkOutDate: formData.checkOutDate!.toISOString().split('T')[0], // Convert to YYYY-MM-DD format
          guestCount: formData.guestCount,
        },
        additionalNotes: formData.additionalNotes.trim() || undefined,
      };
      
      // Make actual API call to create inquiry using the API client
      await apiClient.post('/inquiries', inquiryData);
      
      // Call success callback
      onSuccess?.();
      
      // Close form
      onClose();
      
      // Reset form
      setFormData({
        checkInDate: null,
        checkOutDate: null,
        guestCount: 1,
        whatsappNumber: '',
        instagramId: '',
        additionalNotes: '',
      });
      
    } catch (error: any) {
      console.error('Error submitting inquiry:', error);
      
      // Handle specific error types
      if (error?.status === 401 || error?.code === 'NotAuthorizedException') {
        setSubmitError('Your session has expired. Please refresh the page and try again.');
      } else if (error?.status === 403) {
        setSubmitError('You do not have permission to submit inquiries.');
      } else if (error?.status >= 500) {
        setSubmitError('Server error occurred. Please try again later.');
      } else {
        setSubmitError('Failed to send inquiry. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm, listing.listingId, listing.title, userProfile, formData, onSuccess, onClose]);

  // Handle form close
  const handleClose = useCallback(() => {
    setSubmitError(null);
    setFormErrors({});
    onClose();
  }, [onClose]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            maxHeight: isMobile ? '100vh' : '90vh',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight="bold">
              Contact Host
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pb: 1 }}>
          {/* Property Info */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {listing.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {listing.address.city}, {listing.address.state}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Up to {listing.maxGuests} guests • {listing.bedrooms} bedrooms • {listing.bathrooms} bathrooms
            </Typography>
          </Box>

          {/* Error Alert */}
          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {submitError}
            </Alert>
          )}

          {/* Form Fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Show prefilled info message if dates/guests are prefilled */}
            {(initialData.checkInDate || initialData.checkOutDate || (initialData.guestCount && initialData.guestCount > 1)) && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Your search dates and guest count have been prefilled. You can modify them if needed.
              </Alert>
            )}

            {/* Date Selection */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Stay Dates *
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <DatePicker
                  label="Check-in"
                  value={formData.checkInDate}
                  onChange={(date) => handleFieldChange('checkInDate', date)}
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: !!formErrors.checkInDate,
                      helperText: formErrors.checkInDate,
                    },
                  }}
                />
                
                <DatePicker
                  label="Check-out"
                  value={formData.checkOutDate}
                  onChange={(date) => handleFieldChange('checkOutDate', date)}
                  minDate={formData.checkInDate || new Date()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: !!formErrors.checkOutDate,
                      helperText: formErrors.checkOutDate,
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Guest Count */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Number of Guests *
              </Typography>
              <TextField
                type="number"
                value={formData.guestCount}
                onChange={(e) => handleFieldChange('guestCount', parseInt(e.target.value) || 1)}
                inputProps={{
                  min: 1,
                  max: listing.maxGuests,
                }}
                size="small"
                error={!!formErrors.guestCount}
                helperText={formErrors.guestCount || `Maximum ${listing.maxGuests} guests`}
                fullWidth
              />
            </Box>

            {/* WhatsApp Number */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                WhatsApp Number *
              </Typography>
              <TextField
                value={formData.whatsappNumber}
                onChange={(e) => handleFieldChange('whatsappNumber', e.target.value)}
                placeholder="+1 (555) 123-4567"
                size="small"
                error={!!formErrors.whatsappNumber}
                helperText={formErrors.whatsappNumber || 'Host will contact you via WhatsApp'}
                fullWidth
              />
            </Box>

            {/* Instagram ID (Optional) */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Instagram ID (Optional)
              </Typography>
              <TextField
                value={formData.instagramId}
                onChange={(e) => handleFieldChange('instagramId', e.target.value)}
                placeholder="@yourusername"
                size="small"
                fullWidth
              />
            </Box>

            {/* Additional Notes */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Additional Notes (Optional)
              </Typography>
              <TextField
                value={formData.additionalNotes}
                onChange={(e) => handleFieldChange('additionalNotes', e.target.value)}
                placeholder="Any special requests or questions..."
                multiline
                rows={3}
                size="small"
                fullWidth
              />
            </Box>
          </Box>

          {/* Disclaimer */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="caption" color="info.dark">
              <strong>Note:</strong> You won't be charged yet. The host will contact you via WhatsApp to confirm availability and discuss payment details.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            size="large"
            sx={{ mr: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="large"
            disabled={isSubmitting}
            sx={{
              minWidth: 120,
              fontWeight: 600,
            }}
          >
            {isSubmitting ? 'Sending...' : 'Send Inquiry'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ContactHostForm;